import React from 'react';
interface LatexRendererProps {
  content: string;
  className?: string;
}
/**
 * Simple math renderer without external dependencies.
 * Converts common math notation to HTML/CSS.
 *
 * Supports: \frac{}{}, ^superscript, \times, \cdot, \div, inline $...$
 * Handles compound expressions: $\frac{1}{2} \times \frac{1}{3}$
 * Falls back to plain text on any rendering error.
 */
export function LatexRenderer({
  content,
  className = ''
}: LatexRendererProps) {
  if (!content) return null;
  try {
    return <span className={className} data-id="element-1974">{renderMath(content)}</span>;
  } catch {
    // Fallback: strip LaTeX commands and render as plain text
    return <span className={className} data-id="element-1975">{stripLatex(content)}</span>;
  }
}
/**
 * Strip LaTeX commands from text for plain-text fallback.
 * Converts \frac{a}{b} → a/b, removes $, converts \times → ×, etc.
 */
function stripLatex(text: string): string {
  return text.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1/$2').replace(/\\times/g, '×').replace(/\\cdot/g, '·').replace(/\\div/g, '÷').replace(/\$/g, '').trim();
}
/**
 * Render a fraction as stacked numerator/denominator.
 */
function renderFraction(numerator: string, denominator: string, key: string): React.ReactNode {
  return <span key={key} className="inline-flex flex-col items-center mx-0.5 align-middle" style={{
    verticalAlign: 'middle'
  }} data-id="element-1976">
      <span className="text-[0.85em] leading-tight border-b border-current px-1" data-id="element-1977">
        {renderMathTokens(numerator, `${key}-num`)}
      </span>
      <span className="text-[0.85em] leading-tight px-1" data-id="element-1978">
        {renderMathTokens(denominator, `${key}-den`)}
      </span>
    </span>;
}
/**
 * Parse and render math tokens within a math context (inside $...$).
 * Handles: \frac{a}{b}, \times, \cdot, \div, ^superscript, plain text/numbers.
 */
function renderMathTokens(content: string, baseKey: string): React.ReactNode {
  const elements: React.ReactNode[] = [];
  let remaining = content;
  let idx = 0;
  while (remaining.length > 0) {
    // Skip leading whitespace
    const wsMatch = remaining.match(/^\s+/);
    if (wsMatch) {
      remaining = remaining.substring(wsMatch[0].length);
      if (remaining.length === 0) break;
    }
    // Try: \frac{numerator}{denominator}
    const fracMatch = remaining.match(/^\\frac\{([^}]*)\}\{([^}]*)\}/);
    if (fracMatch) {
      elements.push(renderFraction(fracMatch[1], fracMatch[2], `${baseKey}-f${idx}`));
      remaining = remaining.substring(fracMatch[0].length);
      idx++;
      continue;
    }
    // Try: \times
    if (remaining.startsWith('\\times')) {
      elements.push(<span key={`${baseKey}-t${idx}`} className="mx-1" data-id="element-1979">
          ×
        </span>);
      remaining = remaining.substring(6);
      idx++;
      continue;
    }
    // Try: \cdot
    if (remaining.startsWith('\\cdot')) {
      elements.push(<span key={`${baseKey}-c${idx}`} className="mx-1" data-id="element-1980">
          ·
        </span>);
      remaining = remaining.substring(5);
      idx++;
      continue;
    }
    // Try: \div
    if (remaining.startsWith('\\div')) {
      elements.push(<span key={`${baseKey}-d${idx}`} className="mx-1" data-id="element-1981">
          ÷
        </span>);
      remaining = remaining.substring(4);
      idx++;
      continue;
    }
    // Try: \pi
    if (remaining.startsWith('\\pi')) {
      elements.push(<span key={`${baseKey}-pi${idx}`} data-id="element-1982">π</span>);
      remaining = remaining.substring(3);
      idx++;
      continue;
    }
    // Try: \sqrt{content}
    const sqrtMatch = remaining.match(/^\\sqrt\{([^}]*)\}/);
    if (sqrtMatch) {
      elements.push(<span key={`${baseKey}-sq${idx}`} className="inline-flex items-center" data-id="element-1983">
          <span className="mr-0.5" data-id="element-1984">√</span>
          <span className="border-t border-current px-0.5" data-id="element-1985">
            {renderMathTokens(sqrtMatch[1], `${baseKey}-sq${idx}`)}
          </span>
        </span>);
      remaining = remaining.substring(sqrtMatch[0].length);
      idx++;
      continue;
    }
    // Try: superscript — word^number or }^number or number^number
    const supMatch = remaining.match(/^([a-zA-Z0-9]+)\^(\{[^}]*\}|\d+)/);
    if (supMatch) {
      const supContent = supMatch[2].startsWith('{') ? supMatch[2].slice(1, -1) : supMatch[2];
      elements.push(<span key={`${baseKey}-s${idx}`} data-id="element-1986">
          {supMatch[1]}
          <sup className="text-[0.7em]" data-id="element-1987">{supContent}</sup>
        </span>);
      remaining = remaining.substring(supMatch[0].length);
      idx++;
      continue;
    }
    // Try: standalone ^superscript (after a fraction or other element)
    const standaloneSup = remaining.match(/^\^\{([^}]*)\}/);
    if (standaloneSup) {
      elements.push(<sup key={`${baseKey}-ss${idx}`} className="text-[0.7em]" data-id="element-1988">
          {standaloneSup[1]}
        </sup>);
      remaining = remaining.substring(standaloneSup[0].length);
      idx++;
      continue;
    }
    const standaloneSupDigit = remaining.match(/^\^(\d+)/);
    if (standaloneSupDigit) {
      elements.push(<sup key={`${baseKey}-sd${idx}`} className="text-[0.7em]" data-id="element-1989">
          {standaloneSupDigit[1]}
        </sup>);
      remaining = remaining.substring(standaloneSupDigit[0].length);
      idx++;
      continue;
    }
    // Try: subscript _n or _{content}
    const subMatch = remaining.match(/^_(\{[^}]*\}|\d+)/);
    if (subMatch) {
      const subContent = subMatch[1].startsWith('{') ? subMatch[1].slice(1, -1) : subMatch[1];
      elements.push(<sub key={`${baseKey}-sub${idx}`} className="text-[0.7em]" data-id="element-1990">
          {subContent}
        </sub>);
      remaining = remaining.substring(subMatch[0].length);
      idx++;
      continue;
    }
    // Try: unknown \command — skip the backslash command
    const unknownCmd = remaining.match(/^\\([a-zA-Z]+)/);
    if (unknownCmd) {
      // Render the command name as-is (fallback)
      elements.push(<span key={`${baseKey}-u${idx}`} data-id="element-1991">{unknownCmd[1]}</span>);
      remaining = remaining.substring(unknownCmd[0].length);
      idx++;
      continue;
    }
    // Consume plain text until next special character
    let plainEnd = 0;
    while (plainEnd < remaining.length) {
      const ch = remaining[plainEnd];
      if (ch === '\\' || ch === '^' || ch === '_' || ch === '{' || ch === '}') break;
      plainEnd++;
    }
    if (plainEnd === 0) {
      // Skip unrecognized special character
      plainEnd = 1;
    }
    const plainText = remaining.substring(0, plainEnd);
    if (plainText.trim()) {
      elements.push(<span key={`${baseKey}-p${idx}`} data-id="element-1992">{plainText}</span>);
    } else if (plainText) {
      // Whitespace — render as space
      elements.push(<span key={`${baseKey}-w${idx}`} data-id="element-1993"> </span>);
    }
    remaining = remaining.substring(plainEnd);
    idx++;
  }
  if (elements.length === 0) return content;
  if (elements.length === 1) return elements[0];
  return <>{elements}</>;
}
/**
 * Main render function: finds $...$ inline math blocks and renders them,
 * leaving non-math text as plain text.
 */
function renderMath(text: string): React.ReactNode {
  // Quick check: if no dollar signs, return as-is
  if (!text.includes('$')) return text;
  const parts: React.ReactNode[] = [];
  let currentPos = 0;
  let key = 0;
  // Find all $...$ inline math blocks
  // Use a manual scan to handle edge cases (no regex for nested $)
  while (currentPos < text.length) {
    const dollarPos = text.indexOf('$', currentPos);
    if (dollarPos === -1) {
      // No more math — add remaining text
      parts.push(text.substring(currentPos));
      break;
    }
    // Add text before the math block
    if (dollarPos > currentPos) {
      parts.push(text.substring(currentPos, dollarPos));
    }
    // Find closing $
    const closePos = text.indexOf('$', dollarPos + 1);
    if (closePos === -1) {
      // Unmatched $ — add rest as plain text
      parts.push(text.substring(dollarPos));
      break;
    }
    // Extract math content between $...$
    const mathContent = text.substring(dollarPos + 1, closePos);
    // Render the math content as inline math
    parts.push(<span key={`math-${key}`} className="inline-flex items-center align-baseline" style={{
      verticalAlign: 'middle'
    }} data-id="element-1994">
        {renderMathTokens(mathContent, `m${key}`)}
      </span>);
    currentPos = closePos + 1;
    key++;
  }
  if (parts.length === 0) return text;
  if (parts.length === 1) return parts[0];
  return <>{parts}</>;
}